import React, { useState, useEffect } from 'react';
import CustomDropdown from '../../components/CustomDropdown';
import axios from 'axios';
import { baseUrl } from '../../app/api/apiSlice';
import { useSelector } from 'react-redux';

import {
  Header,
  Label,
  Button,
  Icon,
  Popup,
  Loader,
  Table,
  Segment,
  Grid,
  Modal,
  Card,
  Input,
  Dropdown,
  Message,
  Checkbox,
  Form,
} from 'semantic-ui-react';
import { useLocation } from 'react-router-dom';
import { useNavigate, Link } from 'react-router-dom';

import { useGetChatSessionsQuery } from './chatSessionApiSlice';
import { useCreateEvaluationSessionMutation } from '../evaluation_sessions/evaluationSessionApiSlice';
import { useGetEvaluationCriteriaGroupsQuery } from '../evaluation_criteria_group/evaluationCriteriaGroupApiSlice';
import { useGetContractorsListQuery } from '../users/usersApiSlice';

import { ui } from '../../components/adminUi';
import ChatSessionDetail from './ChatSessionDetail';
import IdentityReviewModal from './IdentityReviewModal';
import MobileChatSessionList, { LINKED_FILTERS, customerLabel, minutesLabel, shortDate } from './MobileChatSessionList';
import { PAGE_SIZE, formatDate, canEvaluate } from '../../constants';
import { NAV } from '../../navLabels';
import ScopeNote from '../../components/ScopeNote';
import { ChipRow, DateRangeField, ListContainer, RatioScore, RowAction, RowButton, Tag, mutedCell, scoreColor, ListPagination, PageBand, SearchField, SortTh, chip, firstCell, listPageStyle, pillButton, tableCard, td, th } from '../../components/listPage';
import '../../components/SharedCard.css';
import CustomSnackbar from '../../components/CustomSnackbar';
import { uploadFileToS3Presigned } from '../../utils/presignedUpload';

// 신원확인은 PII 비식별 파트너에는 해당하지 않는다 — 그 파트너는 신원 정보를 다루지 않는다.
// Keyed off the *session trainee's* contractor, not the viewer's: a superuser sees sessions
// from several contractors at once, so this can differ row to row.
/** The column is Korean; the API's statuses are not. */
export const IDENTITY_LABELS = {
  VERIFIED: '확인됨',
  FLAGGED: '이상 있음',
  PENDING: '확인 전',
};
export const IDENTITY_TONES = { VERIFIED: 'green', FLAGGED: 'red', PENDING: 'gray' };

export const identityReviewable = chatSession =>
  !chatSession?.trainee?.contractor?.anonymize_pii;

// 전체 / 평가 / 연습, as on the phone list (MobileChatSessionList).
const KINDS = [['전체', null], ['평가', 'false'], ['연습', 'true']];
const center = { textAlign: 'center' };

const ChatSessionsList = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const token = useSelector(state => state.auth.token);
  const user = useSelector(state => state.auth.user);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const queryParams = new URLSearchParams(location.search);
  const page = queryParams.get('page');
  const page_size = queryParams.get('page_size');
  const persona = queryParams.get('persona');
  const trainee = queryParams.get('trainee');
  const partner = queryParams.get('partner');
  const contractor = queryParams.get('contractor');
  const start_date = queryParams.get('start_date');
  const end_date = queryParams.get('end_date');
  const search = queryParams.get('search');
  const ordering = queryParams.get('ordering');
  const product = queryParams.get('product');
  const is_practice = queryParams.get('is_practice');
  const missed_criterion = queryParams.get('missed_criterion');

  const [searchValue, setSearchValue] = useState(search || '');

  // get query params from frontend url -> remove null ones to pass to backend
  let qp = {
    page,
    page_size,
    persona,
    trainee,
    partner,
    search,
    ordering,
    contractor,
    start_date,
    end_date,
    product,
    is_practice,
    missed_criterion,
    // Sessions with no lines are noise here; exports send the same filters.
    has_lines: 1,
  };
  qp = Object.entries(qp)
    .filter(e => e[1])
    .reduce((accumulator, [k, v]) => {
      accumulator[k] = v;
      return accumulator;
    }, {});

  const { data: contractorsData } = useGetContractorsListQuery();
  const contractors = Array.isArray(contractorsData)
    ? contractorsData
    : contractorsData?.results || [];
  const contractorOptions = contractors.map(c => ({
    key: c.id,
    text: c.name,
    value: c.id,
  }));

  const handleFilterChange = (key, value) => {
    const searchParams = new URLSearchParams(location.search);
    if (value) {
      searchParams.set(key, value);
    } else {
      searchParams.delete(key);
    }
    searchParams.set('page', 1);
    navigate(`${location.pathname}?${searchParams.toString()}`);
  };

  const handleSearch = () => {
    const searchParams = new URLSearchParams(location.search);
    if (searchValue) {
      searchParams.set('search', searchValue);
    } else {
      searchParams.delete('search');
    }
    searchParams.set('page', 1);
    navigate(`${location.pathname}?${searchParams.toString()}`);
  };

  const handleSort = clickedColumn => {
    const searchParams = new URLSearchParams(location.search);
    const currentOrdering = searchParams.get('ordering');

    let newOrdering = clickedColumn;
    if (currentOrdering === clickedColumn) {
      newOrdering = `-${clickedColumn}`;
    } else if (currentOrdering === `-${clickedColumn}`) {
      newOrdering = clickedColumn;
    }

    searchParams.set('ordering', newOrdering);
    navigate(`${location.pathname}?${searchParams.toString()}`);
  };

  // Zoom Upload Modal State
  const [modalOpen, setModalOpen] = useState(false);
  const [usersList, setUsersList] = useState([]);
  const [formData, setFormData] = useState({
    username: '',
    contractor: '',
    isNewUser: false,
    selectedUserUsername: '',
    file: null,
  });
  const [uploadStatus, setUploadStatus] = useState({
    loading: false,
    error: null,
    success: false,
  });

  useEffect(() => {
    if (modalOpen) {
      // Fetch users
      axios
        .get(`${baseUrl}/api/v1/accounts/users/`, {
          headers: { Authorization: `Bearer ${token}` },
        })
        .then(res => {
          const results = res.data.results || res.data;
          const formatted = (Array.isArray(results) ? results : []).map(u => ({
            key: u.id,
            text: `${u.full_name || u.username} (${u.username})`,
            value: u.username,
          }));
          setUsersList(formatted);
        })
        .catch(err => console.error('Failed to fetch users', err));
    }
  }, [modalOpen, token]);

  const handleUploadSubmit = async () => {
    if (!formData.file) {
      setUploadStatus({
        loading: false,
        error: '파일을 선택해주세요.',
        success: false,
      });
      return;
    }

    if (!formData.contractor) {
      setUploadStatus({
        loading: false,
        error: '파트너를 선택해주세요.',
        success: false,
      });
      return;
    }

    const targetUsername = formData.isNewUser
      ? formData.username
      : formData.selectedUserUsername;
    if (!targetUsername) {
      setUploadStatus({
        loading: false,
        error: '사용자를 선택하거나 입력해주세요.',
        success: false,
      });
      return;
    }

    setUploadStatus({ loading: true, error: null, success: false });
    try {
      // 1. Upload file to S3 (presigned)
      const result = await uploadFileToS3Presigned(formData.file, token);
      if (!result.success) {
        throw new Error(result.error || 'S3 upload failed');
      }

      // 2. Confirm upload and trigger processing
      const confirmRes = await axios.post(
        `${baseUrl}/api/confirm-upload/`,
        {
          key: result.key,
          username: targetUsername,
          contractor_id: formData.contractor,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!confirmRes.data.task_id) {
        throw new Error('Failed to start processing task');
      }

      setUploadStatus({ loading: false, error: null, success: true });
      setTimeout(() => {
        setModalOpen(false);
        setUploadStatus({ loading: false, error: null, success: false });
        setFormData({
          username: '',
          contractor: '',
          isNewUser: false,
          selectedUserUsername: '',
          file: null,
        });
      }, 2000);
    } catch (err) {
      console.error(err);
      setUploadStatus({
        loading: false,
        error: err.response?.data?.error || '업로드 중 오류가 발생했습니다.',
        success: false,
      });
    }
  };

  // GET chat sessions
  const { data, isLoading, isSuccess, isError, error } =
    useGetChatSessionsQuery(
      { ...qp },
      {
        pollingInterval: 5000,
        // Phones use MobileChatSessionList, which fetches its own pages.
        skip: isMobile,
      },
    );

  // to handle chat session selection
  // const [selectedChatSession, setSelectedChatSession] = useState(null);
  const [snackbarVisible, setSnackbarVisible] = useState(false);

  const isAdmin = user?.is_staff || user?.is_superuser || user?.is_contractor_admin;
  const showIdentityColumn =
    isAdmin && (data?.results || []).some(identityReviewable);
  const [reviewModalOpen, setReviewModalOpen] = useState(false);
  const [sessionToReview, setSessionToReview] = useState(null);
  const [reviewSnackbarVisible, setReviewSnackbarVisible] = useState(false);

  const handleSelectChatSession = chat_session => {
    if (chat_session.evaluation_session_count > 0) {
      navigate(`/chatsessions/${chat_session.id}`);
    } else {
      setSnackbarVisible(true);
      setTimeout(() => setSnackbarVisible(false), 3000);
    }
  };

  // To make new evaluation sessions when the user click the button
  const { data: criteria_groups, isLoading: isLoading_cg } =
    useGetEvaluationCriteriaGroupsQuery();
  const [createEvaluationSession] = useCreateEvaluationSessionMutation();

  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [sessionToEvaluate, setSessionToEvaluate] = useState(null);

  const handleOpenConfirmModal = chat_session => {
    setSessionToEvaluate(chat_session);
    setConfirmModalOpen(true);
  };

  const handleConfirmEvaluation = () => {
    if (sessionToEvaluate && criteria_groups?.results?.length > 0) {
      const id = sessionToEvaluate.id;
      // To this roleplay's 결과 page, which shows the evaluation as it finishes (it polls).
      createEvaluationSession({
        chat_session: id,
        criteria_group: criteria_groups.results[0].id,
      }).unwrap().then(() => navigate(`/chatsessions/${id}`)).catch(() => {}); // errors toast in apiSlice
      setConfirmModalOpen(false);
      setSessionToEvaluate(null);
    }
  };

  // Same pill geometry as the 녹음 파일 업로드 CTA next to it, in the neutral tone from
  // adminUi so the primary action still reads as primary.
  // Excel export (client request 17: 개별 및 일괄 다운로드).
  //
  // Server-generated, so the layout can match the ACE Challenge workbooks Apple sent us
  // and the browser needs no spreadsheet library. It has to go through fetch rather than
  // a plain link because the API is bearer-authenticated — an <a href> would 401.
  //
  // The current filter set is sent as-is, minus paging, so the file matches the screen.
  const [exporting, setExporting] = useState(null);
  const [exportFailed, setExportFailed] = useState(false);
  const handleExport = async (kind, extraParams = {}) => {
    setExporting(kind);
    try {
      const params = new URLSearchParams({ ...qp, ...extraParams });
      params.delete('page');
      params.delete('page_size');
      const response = await fetch(
        `${baseUrl}/api/v1/resources/chat_sessions/detailed/export_${kind}/?${params}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      // Named here rather than from Content-Disposition: the API is a different origin,
      // and that header is not exposed to JS unless the server sets
      // Access-Control-Expose-Headers. Building it client-side keeps the date stamp
      // without adding a CORS setting that ops would have to remember.
      const stamp = new Date()
        .toLocaleDateString('sv-SE')  // sv-SE is ISO-shaped: 2026-08-03
        .replace(/-/g, '');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `session_${kind}_${stamp}.xlsx`;
      // Appended before clicking: a detached anchor does not reliably trigger a
      // download outside Chrome, which is why the old dead code here was unreliable.
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      // Deferred so the download has actually started before the blob is released.
      setTimeout(() => window.URL.revokeObjectURL(url), 0);
    } catch (err) {
      setExportFailed(true);
    } finally {
      setExporting(null);
    }
  };

  const handlePaginationChange = (e, { activePage }) => {
    const searchParams = new URLSearchParams(location.search);
    searchParams.set('page', activePage);
    // page_size is usually preserved or set explicitly.
    // The previous code hardcoded PAGE_SIZE.
    if (!searchParams.get('page_size')) {
      searchParams.set('page_size', PAGE_SIZE);
    }
    navigate(`${location.pathname}?${searchParams.toString()}`);
  };

  // display language is in korean
  if (isLoading || isLoading_cg) return <Loader active />;
  if (isError) return null;

  return (
    <div style={listPageStyle(isMobile)}>
      {/* One band for phones and desktop (components/listPage.js), as on 퀴즈 기록. */}
      <PageBand
        isMobile={isMobile}
        icon="comments outline"
        title={NAV.SESSIONS}
        subtitle={NAV.SESSIONS_SUBTITLE}
        actions={!isMobile && isAdmin && (
          <>
            <button type="button" disabled={!!exporting} onClick={() => handleExport('summary')} style={{ ...pillButton(false, true), opacity: exporting ? 0.6 : 1 }}>
              요약 다운로드
              <Icon name={exporting === 'summary' ? 'spinner' : 'file alternate outline'} loading={exporting === 'summary'} className="centered-icon" />
            </button>
            {/* The workbook holds one 평가표 per evaluated session, so it can have fewer
                sheets than the list has rows — say so before they count. Stacked pages,
                not 요약's single page, so the two buttons don't look interchangeable. */}
            <Popup
              content="평가 완료된 롤플레이만 평가표로 받습니다."
              trigger={
                <button type="button" disabled={!!exporting} onClick={() => handleExport('detail')} style={{ ...pillButton(false, true), opacity: exporting ? 0.6 : 1 }}>
                  상세 다운로드
                  <Icon name={exporting === 'detail' ? 'spinner' : 'copy outline'} loading={exporting === 'detail'} className="centered-icon" />
                </button>
              }
            />
            {/* Admin only, as on phones: uploading a store recording for evaluation. */}
            <button type="button" onClick={() => setModalOpen(true)} style={pillButton(true, true)}>
              녹음 파일 업로드
              <Icon name="upload" className="centered-icon" />
            </button>
          </>
        )}
      />

      <ListContainer isMobile={isMobile}>
        {!isMobile && (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
              <DateRangeField
                start={queryParams.get('start_date')}
                end={queryParams.get('end_date')}
                onChange={handleFilterChange}
                onClear={() => {
                  const searchParams = new URLSearchParams(location.search);
                  searchParams.delete('start_date');
                  searchParams.delete('end_date');
                  searchParams.set('page', 1);
                  navigate(`${location.pathname}?${searchParams.toString()}`);
                }}
              />
              {(user?.is_staff || user?.is_superuser) && (
                <CustomDropdown
                  searchable
                  placeholder="파트너 선택"
                  options={contractorOptions}
                  value={contractor}
                  onChange={value => handleFilterChange('contractor', value)}
                  icon="building outline"
                  style={{ minWidth: '180px', flex: '0 0 auto' }}
                />
              )}
              <SearchField value={searchValue} onChange={setSearchValue} onSubmit={handleSearch} placeholder="고객, 직원, 대화 내용 검색" />
              <button type="button" onClick={handleSearch} style={pillButton(true)}>검색</button>
            </div>
            <ScopeNote linked={LINKED_FILTERS} />
            <ChipRow
              count={data?.count}
              groups={[
                KINDS.map(([label, value]) => (
                  <button key={label} type="button" onClick={() => handleFilterChange('is_practice', value)} style={chip((is_practice || null) === value)}>
                    {label}
                  </button>
                )),
              ]}
            />
          </>
        )}
        {isMobile ? (
          <MobileChatSessionList
            isAdmin={isAdmin}
            onOpen={handleSelectChatSession}
            onEvaluate={handleOpenConfirmModal}
            onUpload={() => setModalOpen(true)}
          />
        ) : (
          <>
            <div style={tableCard}>
              {data.results.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px', color: '#86868b' }}>롤플레이 기록이 없습니다.</div>
              ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      <th style={firstCell(th)}>ID</th>
                      <SortTh label="일시" field="created_at" ordering={ordering} onSort={handleSort} />
                      <SortTh label="고객" field="persona__name" ordering={ordering} onSort={handleSort} />
                      <SortTh label="직원" field="trainee__username" ordering={ordering} onSort={handleSort} />
                      <th style={th}>파트너</th>
                      <SortTh label="구분" field="is_practice" ordering={ordering} onSort={handleSort} style={center} />
                      <SortTh label="발화 수" field="chatlog_count" ordering={ordering} onSort={handleSort} style={center} />
                      <SortTh label="소요시간" field="duration" ordering={ordering} onSort={handleSort} style={center} />
                      <SortTh label="응대 스킬" field="latest_evaluation_percent" ordering={ordering} onSort={handleSort} style={center} />
                      <SortTh label="설명 정확도" field="latest_pk_evaluation_score" ordering={ordering} onSort={handleSort} style={center} />
                      {showIdentityColumn && <th style={{ ...th, ...center }}>신원확인</th>}
                      {/* One column per action, so a button always sits in the same place and
                          they line up down the table. Sharing one cell made them shift
                          horizontally row to row, since each is conditional. */}
                      <th style={{ ...th, ...center }}>이어하기</th>
                      <th style={{ ...th, ...center }}>평가</th>
                      {isAdmin && <th style={{ ...th, ...center }}>평가표</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {data.results.map(chat_session => (
                      <tr key={chat_session.id} onClick={() => handleSelectChatSession(chat_session)} style={{ cursor: 'pointer' }}>
                        <td style={firstCell(td)}>
                          {/* A support handle, not a status chip. */}
                          <span
                            onClick={e => {
                              e.stopPropagation();
                              navigator.clipboard.writeText(chat_session.id);
                            }}
                            title="클릭하면 롤플레이 ID가 복사됩니다"
                            style={{ cursor: 'pointer', color: ui.muted, fontSize: '12px', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' }}
                          >
                            {chat_session.id.slice(0, 5)}
                          </span>
                        </td>
                        <td style={{ ...td, ...mutedCell }}>{shortDate(chat_session.created_at)}</td>
                        <td style={{ ...td, whiteSpace: 'nowrap' }}>
                          {/* N번 고객 — what the trainee saw, what the client cites, and what the
                              exported sheets are named by. The generated name and the visit
                              reason are in the tooltip. */}
                          {chat_session.persona ? (
                            <Popup
                              header={chat_session.persona.name}
                              content={chat_session.persona.persona?.purpose_of_visit?.value || chat_session.persona.purpose_of_visit}
                              trigger={<span>{customerLabel(chat_session.persona)}</span>}
                            />
                          ) : (
                            <span style={{ color: ui.muted }}>실제 고객</span>
                          )}
                        </td>
                        <td style={{ ...td, whiteSpace: 'nowrap' }}>
                          {chat_session.trainee ? (
                            <Link to={`/userdashboard?userId=${chat_session.trainee.id}`} onClick={e => e.stopPropagation()} style={{ color: '#1d1d1f', fontWeight: 600 }}>
                              {chat_session.trainee.full_name || chat_session.trainee.username}
                            </Link>
                          ) : (
                            <span style={{ color: ui.muted }}>실제 인물</span>
                          )}
                        </td>
                        <td style={{ ...td, whiteSpace: 'nowrap' }}>{chat_session.trainee?.contractor?.name || <span style={{ color: ui.muted }}>—</span>}</td>
                        <td style={{ ...td, ...center }}>
                          <Tag tone={chat_session.is_practice ? 'gray' : 'blue'}>{chat_session.is_practice ? '연습' : '평가'}</Tag>
                        </td>
                        {/* Values, not states: plain numbers, not boxed chips. */}
                        <td style={{ ...td, ...center }}>{chat_session.chatlog_count}</td>
                        <td style={{ ...td, ...mutedCell, ...center }}>{minutesLabel(chat_session.duration) || '—'}</td>
                        <td style={{ ...td, ...center }}>
                          {/* 달성률 % (score ÷ its criteria group's maximum), as 직원 홈 and 직원 관리. */}
                          {chat_session.latest_evaluation_percent != null ? (
                            <span style={{ fontWeight: 600, color: scoreColor(chat_session.latest_evaluation_percent) }}>{Math.round(chat_session.latest_evaluation_percent)}%</span>
                          ) : (
                            <span style={{ color: ui.muted }}>—</span>
                          )}
                        </td>
                        {/* 제품 지식 as correct / checked claims, as 직원 관리 shows it. */}
                        <td style={{ ...td, ...center }}>
                          <RatioScore num={chat_session.pk_correct} den={chat_session.pk_checked} />
                        </td>
                        {showIdentityColumn && (
                          <td style={{ ...td, ...center }} onClick={e => e.stopPropagation()}>
                            {!identityReviewable(chat_session) ? (
                              <span style={{ color: ui.muted }}>—</span>
                            ) : (
                              <button
                                type="button"
                                onClick={e => {
                                  e.stopPropagation();
                                  setSessionToReview(chat_session.id);
                                  setReviewModalOpen(true);
                                }}
                                style={{ border: 'none', background: 'none', padding: 0, cursor: 'pointer' }}
                              >
                                <Tag tone={IDENTITY_TONES[chat_session.identity_verified_status] || 'gray'}>
                                  {IDENTITY_LABELS[chat_session.identity_verified_status] || '확인 전'}
                                </Tag>
                              </button>
                            )}
                          </td>
                        )}
                        <td style={{ ...td, ...center }} onClick={e => e.stopPropagation()}>
                          <Popup
                            content={
                              // Lines added after grading wouldn't be in the score.
                              chat_session.evaluation_session_count > 0 ? '이미 평가가 실행된 롤플레이는 이어할 수 없습니다' : '이 롤플레이를 이어합니다.'
                            }
                            trigger={
                              <RowButton
                                icon="comments outline"
                                disabled={chat_session.evaluation_session_count > 0}
                                onClick={() => navigate(`/audio-orb-openai?session_id=${chat_session.id}`)}
                              >
                                이어하기
                              </RowButton>
                            }
                          />
                        </td>
                        <td style={{ ...td, ...center }} onClick={e => e.stopPropagation()}>
                          {/* 연습 sessions are never graded — the server refuses them. */}
                          {chat_session.is_practice ? (
                            <span style={{ color: ui.muted }}>—</span>
                          ) : (
                            <Popup
                              content={
                                chat_session.chatlog_count < 6
                                  ? '평가를 위해 최소 3턴 이상 필요'
                                  : chat_session.evaluation_session_count > 0
                                    ? '이미 평가가 실행된 롤플레이입니다'
                                    : '평가 실행'
                              }
                              trigger={
                                // Primary only when it is the first evaluation and the session is
                                // long enough — otherwise none of the row's buttons would lead.
                                <RowButton
                                  primary={canEvaluate(chat_session)}
                                  icon="clipboard check"
                                  disabled={!canEvaluate(chat_session)}
                                  onClick={() => handleOpenConfirmModal(chat_session)}
                                >
                                  평가 실행
                                </RowButton>
                              }
                            />
                          )}
                        </td>
                        {isAdmin && (
                          <td style={{ ...td, ...center }} onClick={e => e.stopPropagation()}>
                            {!chat_session.is_practice && chat_session.evaluation_session_count > 0 ? (
                              <Popup
                                content="이 롤플레이 평가표 다운로드"
                                trigger={
                                  <RowAction icon="file alternate outline" title="평가표 다운로드" disabled={!!exporting} onClick={() => handleExport('detail', { id: chat_session.id })} />
                                }
                              />
                            ) : (
                              <span style={{ color: ui.muted }}>—</span>
                            )}
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              )}
            </div>
            <ListPagination page={Number(page) || 1} count={data.count} pageSize={Number(page_size) || PAGE_SIZE} onChange={pg => handlePaginationChange(null, { activePage: pg })} />
          </>
        )}
        <Modal
          open={confirmModalOpen}
          onClose={() => setConfirmModalOpen(false)}
          size="small"
        >
          <Header icon="magic" content="평가 실행 확인" />
          <Modal.Content>
            {sessionToEvaluate && (
              <>
                <p>이 롤플레이로 평가를 실행하시겠습니까?</p>
                <Table definition>
                  <Table.Body>
                    <Table.Row>
                      <Table.Cell width={4}>ID</Table.Cell>
                      <Table.Cell>
                        {sessionToEvaluate.id.slice(0, 8)}
                      </Table.Cell>
                    </Table.Row>
                    <Table.Row>
                      <Table.Cell>일시</Table.Cell>
                      <Table.Cell>
                        {formatDate(sessionToEvaluate.created_at)}
                      </Table.Cell>
                    </Table.Row>
                    <Table.Row>
                      <Table.Cell>교육생</Table.Cell>
                      <Table.Cell>
                        {sessionToEvaluate.trainee?.full_name ||
                          sessionToEvaluate.trainee?.username}
                      </Table.Cell>
                    </Table.Row>
                    <Table.Row>
                      <Table.Cell>페르소나</Table.Cell>
                      <Table.Cell>{sessionToEvaluate.persona?.name}</Table.Cell>
                    </Table.Row>
                    <Table.Row>
                      <Table.Cell>기존 평가 수</Table.Cell>
                      <Table.Cell>
                        {sessionToEvaluate.evaluation_session_count}회
                      </Table.Cell>
                    </Table.Row>
                  </Table.Body>
                </Table>
              </>
            )}
          </Modal.Content>
          <Modal.Actions>
            <Button color="red" onClick={() => setConfirmModalOpen(false)}>
              <Icon name="remove" /> 취소
            </Button>
            <Button color="green" onClick={handleConfirmEvaluation}>
              <Icon name="checkmark" /> 평가 실행
            </Button>
          </Modal.Actions>
        </Modal>
        <CustomSnackbar
          visible={snackbarVisible}
          title="평가 결과 없음"
          message="이 롤플레이에 대한 평가 결과가 없습니다."
          onClose={() => setSnackbarVisible(false)}
          type="warning"
        />
        {/* Upload Modal */}
        <Modal open={modalOpen} onClose={() => setModalOpen(false)} size="tiny">
          <Modal.Header>녹음 파일 업로드</Modal.Header>
          <Modal.Content>
            <Form error={!!uploadStatus.error} success={uploadStatus.success}>
              <Form.Field required>
                <label>파트너 선택</label>
                <Dropdown
                  placeholder="파트너 선택"
                  fluid
                  selection
                  options={contractorOptions}
                  value={formData.contractor}
                  onChange={(e, { value }) =>
                    setFormData({ ...formData, contractor: value })
                  }
                />
              </Form.Field>

              <Form.Field>
                <Checkbox
                  toggle
                  label="새로운 사용자 생성"
                  checked={formData.isNewUser}
                  onChange={(e, { checked }) =>
                    setFormData({ ...formData, isNewUser: checked })
                  }
                />
              </Form.Field>

              {formData.isNewUser ? (
                <Form.Field required>
                  <label>새 사용자 이름 (Username)</label>
                  <Input
                    placeholder="Username"
                    value={formData.username}
                    onChange={e =>
                      setFormData({ ...formData, username: e.target.value })
                    }
                  />
                </Form.Field>
              ) : (
                <Form.Field required>
                  <label>기존 사용자 선택</label>
                  <Dropdown
                    placeholder="사용자 검색 및 선택"
                    fluid
                    search
                    selection
                    options={usersList}
                    value={formData.selectedUserUsername}
                    onChange={(e, { value }) =>
                      setFormData({ ...formData, selectedUserUsername: value })
                    }
                  />
                </Form.Field>
              )}

              <Form.Field required>
                <label>녹화 파일 (.mp4, .mp3, .wav, .m4a)</label>
                <Input
                  type="file"
                  accept=".mp4,.mp3,.wav,.m4a"
                  onChange={e =>
                    setFormData({ ...formData, file: e.target.files[0] })
                  }
                />
              </Form.Field>

              <Message error header="오류 발생" content={uploadStatus.error} />
              <Message
                success
                header="업로드 완료"
                content={
                  <>
                    성공적으로 업로드되었습니다. 잠시 후 창이 닫힙니다.<br />
                    파일 크기에 따라 업로드 완료까지 시간이 소요될 수 있습니다.
                  </>
                }
              />
            </Form>
          </Modal.Content>
          <Modal.Actions>
            <Button
              onClick={() => setModalOpen(false)}
              disabled={uploadStatus.loading}
            >
              취소
            </Button>
            <Button
              content="업로드"
              labelPosition="right"
              icon="checkmark"
              onClick={handleUploadSubmit}
              primary
              loading={uploadStatus.loading}
              disabled={uploadStatus.loading || uploadStatus.success}
            />
          </Modal.Actions>
        </Modal>
        {isAdmin && (
          <>
            <IdentityReviewModal
              sessionId={sessionToReview}
              open={reviewModalOpen}
              onClose={() => {
                setReviewModalOpen(false);
                setSessionToReview(null);
              }}
              onReviewSuccess={() => {
                setReviewSnackbarVisible(true);
                setTimeout(() => setReviewSnackbarVisible(false), 3000);
              }}
            />
            <CustomSnackbar
              visible={reviewSnackbarVisible}
              title="신원 확인 완료"
              message="신원 검토가 저장되었습니다."
              onClose={() => setReviewSnackbarVisible(false)}
              type="success"
            />
            <CustomSnackbar
              visible={exportFailed}
              title="다운로드 실패"
              message="엑셀 다운로드에 실패했습니다. 잠시 후 다시 시도해 주세요."
              onClose={() => setExportFailed(false)}
              type="error"
            />
          </>
        )}
      </ListContainer>
    </div>
  );
};
export default ChatSessionsList;
